import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PublicLayout } from "@/components/Navbar";
import { ProductCard } from "@/components/ProductCard";
import { Link } from "react-router-dom";
import { Search, SlidersHorizontal, X } from "lucide-react";

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ categories: [], brands: [], countries: [], storage_types: [] });
  const [f, setF] = useState({ search: "", category: "", brand: "", country: "", storage: "" });

  const load = () => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/public/products", { params }).then(({ data }) => setProducts(data));
  };
  useEffect(() => { api.get("/public/meta").then(({ data }) => setMeta(data)); }, []);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [f]);

  const upd = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const active = Object.values(f).some(Boolean);
  const clearAll = () => setF({ search: "", category: "", brand: "", country: "", storage: "" });

  const selectCls = "rounded-xl border border-[#2A2E39] bg-[#1A1D24] px-3 py-3 text-sm text-slate-100 focus:border-[#D4AF37]/60 focus:outline-none";
  const dropdowns = [
    { k: "brand", label: "All Brands", opts: meta.brands },
    { k: "category", label: "All Categories", opts: meta.categories },
    { k: "country", label: "All Origins", opts: meta.countries },
    { k: "storage", label: "All Storage", opts: meta.storage_types },
  ];

  return (
    <PublicLayout>
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-8">
        <h1 className="font-serif-display text-4xl font-bold text-slate-100 lg:text-5xl">Wholesale <span className="mc-gold-gradient">Catalogue</span></h1>
        <p className="mt-3 max-w-2xl text-sm text-[#94A3B8]">Browse our full range. Trade pricing and live stock are visible once your account is approved.</p>

        <div className="mt-8 rounded-2xl border border-[#2A2E39] bg-[#121418]/60 p-4 sm:p-5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748B]" />
            <input data-testid="catalog-search" value={f.search} onChange={upd("search")}
              placeholder="Search by product name, brand or SKU…"
              className="w-full rounded-full border border-[#2A2E39] bg-[#1A1D24] py-3 pl-11 pr-4 text-sm text-slate-100 placeholder:text-[#64748B] focus:border-[#D4AF37]/60 focus:outline-none" />
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#D4AF37]"><SlidersHorizontal className="h-4 w-4" /> Filters</span>
            {dropdowns.map((d) => (
              <select key={d.k} data-testid={`filter-${d.k}`} value={f[d.k]} onChange={upd(d.k)} className={selectCls}>
                <option value="">{d.label}</option>
                {(d.opts || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ))}
            {active && (
              <button data-testid="filter-clear" onClick={clearAll}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#2A2E39] px-3 py-2 text-sm text-slate-300 hover:border-rose-500/50 hover:text-rose-400">
                <X className="h-3.5 w-3.5" /> Clear
              </button>
            )}
            <span className="ml-auto text-xs text-[#64748B]">{products.length} product{products.length !== 1 ? "s" : ""}</span>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {products.map((p) => (
            <ProductCard key={p.sku} product={p} to={`/product/${p.sku}`}
              priceSlot={<div className="mt-3 rounded-lg border border-[#2A2E39] bg-[#0B0C0E]/60 px-3 py-2 text-xs text-[#94A3B8]">Trade price on account</div>}>
              <Link to={`/product/${p.sku}`} data-testid={`view-product-${p.sku}`}
                className="inline-flex w-full items-center justify-center rounded-full border border-[#D4AF37]/40 px-4 py-2.5 text-sm font-semibold text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10">View Product</Link>
            </ProductCard>
          ))}
        </div>
        {products.length === 0 && <div className="py-20 text-center text-[#64748B]">No products match your filters.</div>}
      </div>
    </PublicLayout>
  );
}
